<script setup lang="ts">
import { getLocaleValue } from '@/js/helper/locale.ts';
import { classname, formatPrice } from '@/js/utils.ts';
import BaseButton from '@/js/vue/base/base-button.vue';
import BaseIcon from '@/js/vue/base/base-icon.vue';
import ProductQuantity from '@/js/vue/cart/product-quantity.vue';
import { computed } from 'vue';

const props = defineProps<{
  image: string
  price: number
  quantity: number
  name: string
  url: string
}>();
const emit = defineEmits<{ changeQuantity: [value: number], favorite: [], delete: [] }>();
const cn = classname('cart-item');
const formattedTotalPrice = computed(() => formatPrice(props.price * props.quantity));
async function updateQuantity(quantity: number) {
  emit('changeQuantity', quantity);
}
</script>

<template>
  <div :class="cn()">
    <a :href="url" :class="cn('image-container')">
      <img :src="image" :alt="name" width="150" height="150" loading="lazy">
    </a>
    <a :href="url" :class="cn('name')">
      {{ name }}
    </a>
    <div :class="cn('quantity')">
      <ProductQuantity :quantity="quantity" @update:quantity="updateQuantity" />
    </div>
    <div :class="cn('prices')">
      <div :class="cn('price')">
        {{ formattedTotalPrice }}
      </div>
      <div :class="[cn('price', { old: true }), cn('mobile-hide')]">
        {{ formatPrice(price) }}/шт.
      </div>
    </div>
    <div :class="cn('actions')">
      <BaseButton :class="cn('action', { name: 'favorite' })" data-toggle="tooltip" data-original-title="В избранное" @click="$emit('favorite')">
        <BaseIcon type="main" icon="heart" :width="14" :height="18" />
        {{ getLocaleValue('to_favorite') }}
      </BaseButton>
      <BaseButton :class="cn('action', { name: 'delete' })" @click="$emit('delete')">
        <BaseIcon type="main" icon="trash" :width="14" :height="18" />
        <span :class="cn('mobile-hide')">
          {{ getLocaleValue('delete') }}
        </span>
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss">
.cart-item {
  display: grid;
  grid-template-areas: 'image name quantity prices' 'image name quantity actions';
  grid-template-columns: 200px 280px 1fr auto;
  padding: 40px 20px;
  border: 1px solid var(--border-color);

  &:not(:last-child) {
    border-bottom: none;
  }

  &__image-container {
    display: block;
    grid-area: image;

    img {
      display: block;
      max-width: 100%;
      height: auto;
    }
  }

  &__name {
    grid-area: name;
    max-width: 280px;
    color: inherit;
    text-decoration: none;
    font-size: 18px;
  }

  &__quantity {
    grid-area: quantity;
  }

  &__actions {
    grid-area: actions;
  }

  &__prices {
    display: flex;
    flex-direction: column;
    grid-area: prices;
  }

  &__price {
    font-size: 20px;

    &_old {
      font-size: 12px;
    }
  }

  @media (max-width: 1200px) {
    row-gap: 8px;
    column-gap: 12px;
    grid-template-areas: 'image name actions' 'image prices actions' 'image quantity quantity';
    grid-template-columns: 70px 1fr auto;

    &__mobile-hide {
      display: none;
    }

    &__price {
      font-weight: 700;
      color: var(--accent-color);
    }

    &__action {
      &_name {
        &_favorite {
          display: none;
        }
      }
    }
  }

  @media (width <= 992px) {
    padding: 16px 0;
    border: none;
    border-bottom: 1px solid var(--border-color);
  }
}
</style>
